import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Keep the page out of search engines (private/unlisted pages). */
  noindex?: boolean;
}

/**
 * Sets document <head> meta tags for SEO / AEO.
 * Because this is an SPA we manipulate the DOM directly.
 */
export const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = "https://solutions.beam.healthcare/og-image.png",
  ogType,
  jsonLd,
  noindex = false,
}: SEOHeadProps) => {
  useEffect(() => {
    // Title
    document.title = title;

    // Helper to upsert a meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const href = canonical || window.location.origin + window.location.pathname;
    const resolvedOgType =
      ogType || (window.location.pathname.startsWith("/resources/") ? "article" : "website");

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:url", href);
    setMeta("property", "og:type", resolvedOgType);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = href;

    // JSON-LD
    const existingLd = document.querySelectorAll('script[data-seo-ld="true"]');
    existingLd.forEach((el) => el.remove());

    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-ld", "true");
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }

    return () => {
      // Cleanup JSON-LD on unmount
      document.querySelectorAll('script[data-seo-ld="true"]').forEach((el) => el.remove());
    };
  }, [title, description, canonical, ogImage, ogType, jsonLd, noindex]);

  return null;
};
